// @ts-nocheck
import _ from 'lodash';
import React from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { makeStyles, Box, FormControlLabel, Switch, Button, Icon } from '@material-ui/core';
import { GridOverlay, GridToolbarContainer } from '@material-ui/data-grid';
import { useDialog } from '../../../../../common/hooks';
import { DateUtils, StringUtils } from '../../../../../common/utils';
import { DataTable, EmptyState } from '../../../../../common/components';
import SubscriptionProducts from './SubscriptionProducts';

const useStyles = makeStyles(theme => ({
  container: {
    height: '100%',
    '& .MuiDataGrid-root': {
      border: '0',
    },
  },
  switch: {
    padding: theme.spacing(1),
  },
  red: {
    color: theme.palette.error.main,
  },
}));

function SubscriptionList(props) {
  const [t] = useTranslation();
  const { showDialog } = useDialog();
  const classes = useStyles();

  const {
    data,
    channels,
    products,
    showExpired,
    onSelectSubscription,
    onFetchData,
    onFilterChange,
    onCreateSupportProduct,
    actionsDisabled,
    onInvalidateCache,
  } = props;

  const getProviderId = params => {
    const { value: providerId } = params;
    switch (providerId) {
      case 1:
        return 'TV 2';
      case 2:
        return 'TV 2 ansat';
      case 38:
      case 39:
        return 'B2B';
      default:
        return 'Partner';
    }
  };

  const getProvider = params => {
    const { value: provider } = params;
    if (provider && provider.id > 2 && provider.id !== 38 && provider.id !== 39) {
      return provider.name;
    }
    return '';
  };

  const getPendingChanges = params => {
    const { value: pendingChanges } = params;
    if (_.isEmpty(pendingChanges)) {
      return '';
    }
    return StringUtils.getEnumTranslation(t, 'subscription.subscriptionPendingChangesEnum', pendingChanges[0].type, pendingChanges[0].type)
  };

  const getColumnDefs = () => [
    {
      field: 'id',
      headerName: t('subscription.subscriptionId'),
      width: 175,
    },
    {
      field: 'productName',
      headerName: t('subscription.subscriptionProductName'),
      width: 250,
    },
    {
      field: 'amount',
      headerName: t('subscription.subscriptionAmount'),
      width: 125,
      valueGetter: params => StringUtils.getFormattedSum(params.value),
      sortComparator: (v1, v2) => StringUtils.compare(v1, v2, true),
    },
    {
      field: 'discount',
      headerName: t('subscription.subscriptionDiscount'),
      width: 135,
      valueGetter: params => params.value && StringUtils.getFormattedDiscount(params.value.discountValue, params.value.discountType),
      sortComparator: (v1, v2) => StringUtils.compare(v1, v2, true),
    },
    {
      field: 'duration',
      headerName: t('subscription.subscriptionDuration'),
      width: 150,
      valueGetter: params => StringUtils.getFormattedDays(params.value),
      sortComparator: (v1, v2) => StringUtils.compare(v1, v2, true),
    },
    {
      field: 'paymentCount',
      headerName: t('subscription.subscriptionPaymentCount'),
      width: 180,
    },
    {
      field: 'startTime',
      headerName: t('subscription.subscriptionStartTime'),
      width: 150,
      type: 'date',
      valueFormatter: params => DateUtils.getDate(params.value),
    },
    {
      field: 'nextPaymentTime',
      headerName: t('subscription.subscriptionNextPaymentTime'),
      width: 160,
      type: 'date',
      valueFormatter: params => DateUtils.getDate(params.value),
    },
    {
      field: 'providerId',
      headerName: t('subscription.subscriptionProviderId'),
      width: 175,
      valueGetter: getProviderId,
      sortComparator: StringUtils.compare,
    },
    {
      field: 'provider',
      headerName: t('subscription.subscriptionProvider'),
      width: 150,
      valueGetter: getProvider,
      sortComparator: StringUtils.compare,
    },
    {
      field: 'status',
      headerName: t('subscription.subscriptionStatus'),
      width: 150,
      cellClassName: params => clsx({ [classes.red]: params.value !== 'Aktiv' }),
    },
    {
      field: 'type',
      headerName: t('subscription.subscriptionType'),
      width: 150,
    },
    {
      field: 'freePeriod',
      headerName: t('subscription.subscriptionFreePeriod'),
      width: 165,
      filterable: false,
      valueFormatter: params => (params.value ? 'Ja' : 'Nej'),
    },
    {
      field: 'pendingChanges',
      headerName: t('subscription.subscriptionPendingChanges'),
      width: 220,
      cellClassName: classes.red,
      valueGetter: getPendingChanges,
      sortComparator: StringUtils.compare,
    },
  ];

  const getRowData = () => {
    return data.content || [];
  };

  const handleShowCreateSupportProductDialog = () => {
    showDialog({
      title: t('subscription.subscriptionAssignSupportProduct'),
      content: params => (
        <SubscriptionProducts channels={channels} products={products} onChange={params.setValue} />
      ),
      onConfirm: onCreateSupportProduct,
      confirmButtonText: t('common.assign'),
    });
  };

  const handleInvalidateCache = () => {
    onInvalidateCache();
  };

  const renderToolbar = () => (
    <GridToolbarContainer>
      <FormControlLabel
        className={classes.switch}
        control={
          <Switch
            id="SubscriptionList-showExpired"
            color="primary"
            checked={showExpired}
            onChange={onFilterChange}
            disabled={actionsDisabled}
          />
        }
        label={t('subscription.subscriptionsShowExpired')}
      />
      <Button
        id="SubscriptionList-invalidateUserCache"
        variant="outlined"
        color="primary"
        onClick={handleInvalidateCache}
        disabled={actionsDisabled}
        startIcon={<Icon>cached</Icon>}
      >
        {t('subscription.subscriptionInvalidateCache')}
      </Button>
      <Button
        id="SubscriptionList-assignProduct"
        variant="outlined"
        color="primary"
        startIcon={<Icon>add_shopping_cart</Icon>}
        onClick={handleShowCreateSupportProductDialog}
        disabled={actionsDisabled}
      >
        {t('subscription.subscriptionAssignSupportProduct')}
      </Button>
    </GridToolbarContainer>
  );

  const renderNoRowsOverlay = () => (
    <GridOverlay>
      <EmptyState variant="empty" message={t('subscription.subscriptionsEmpty')} />
    </GridOverlay>
  );

  return (
    <Box id="SubscriptionList-container" className={classes.container}>
      <DataTable
        id="SubscriptionList-table"
        components={{
          Toolbar: renderToolbar,
          NoRowsOverlay: renderNoRowsOverlay,
        }}
        columns={getColumnDefs()}
        rows={getRowData()}
        rowCount={data.total}
        pageSize={data.size}
        pageIndex={data.page - 1}
        onRowClick={onSelectSubscription}
        onPageChange={onFetchData}
        disableRowClick={actionsDisabled}
        paginationMode="server"
        pagination
      />
    </Box>
  );
}

SubscriptionList.propTypes = {
  data: PropTypes.shape({
    items: PropTypes.number,
    page: PropTypes.number,
    pages: PropTypes.number,
    size: PropTypes.number,
    total: PropTypes.number,
    content: PropTypes.arrayOf(PropTypes.object),
  }).isRequired,
  channels: PropTypes.arrayOf(PropTypes.object).isRequired,
  products: PropTypes.arrayOf(PropTypes.object).isRequired,
  showExpired: PropTypes.bool.isRequired,
  onSelectSubscription: PropTypes.func.isRequired,
  onFetchData: PropTypes.func.isRequired,
  onFilterChange: PropTypes.func.isRequired,
  onCreateSupportProduct: PropTypes.func.isRequired,
  actionsDisabled: PropTypes.bool.isRequired,
  onInvalidateCache: PropTypes.func.isRequired,
};

export default SubscriptionList;
